import { useTranslation } from 'react-i18next';

import { InputField } from '@saas-kit/ui';

import type { TenantOwnerMode } from './api';

/** Owner section state, owned by TenantCreateForm and edited here. */
export interface OwnerFormState {
  mode: TenantOwnerMode;
  email: string;
  password: string;
  fullName: string;
  userId: string;
}

export const EMPTY_OWNER: OwnerFormState = {
  mode: 'new',
  email: '',
  password: '',
  fullName: '',
  userId: '',
};

const OWNER_MODES: TenantOwnerMode[] = ['new', 'invite', 'existing'];

interface Props {
  value: OwnerFormState;
  onChange: (next: OwnerFormState) => void;
}

/** Owner picker for tenant creation: choose how the workspace owner is
 *  resolved, then collect the fields that mode needs. */
export function TenantOwnerFields({ value, onChange }: Props): JSX.Element {
  const { t } = useTranslation('adminTenants');
  const patch = (next: Partial<OwnerFormState>): void =>
    onChange({ ...value, ...next });

  return (
    <>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-foreground">
          {t('create.owner_mode_label')}
        </legend>
        <div className="flex flex-wrap gap-3 text-sm">
          {OWNER_MODES.map((mode) => (
            <label key={mode} className="flex items-center gap-2">
              <input
                type="radio"
                name="owner_mode"
                value={mode}
                checked={value.mode === mode}
                onChange={() => patch({ mode })}
              />
              {t(`create.owner_mode_${mode}`)}
            </label>
          ))}
        </div>
      </fieldset>

      {value.mode === 'existing' ? (
        <InputField
          label={t('create.owner_user_id_label')}
          hint={t('create.owner_user_id_hint')}
          value={value.userId}
          onChange={(e) => patch({ userId: e.target.value })}
          required
        />
      ) : (
        <InputField
          label={t('create.owner_email_label')}
          hint={t(
            value.mode === 'new'
              ? 'create.owner_email_new_hint'
              : 'create.owner_email_hint',
          )}
          type="email"
          value={value.email}
          onChange={(e) => patch({ email: e.target.value })}
          required
        />
      )}

      {value.mode === 'new' ? (
        <>
          <InputField
            label={t('create.owner_password_label')}
            hint={t('create.owner_password_hint')}
            type="password"
            value={value.password}
            onChange={(e) => patch({ password: e.target.value })}
            required
          />
          <InputField
            label={t('create.owner_full_name_label')}
            hint={t('create.owner_full_name_hint')}
            value={value.fullName}
            onChange={(e) => patch({ fullName: e.target.value })}
          />
        </>
      ) : null}
    </>
  );
}
